/** Collapses repeat notifications onto one OS slot per conversation. */
const NOTIFICATION_TAG_PREFIX = 'cadstrom-agent-run';

/**
 * Whether this browser exposes the notification API at all.
 *
 * @returns True when notifications can be requested and shown.
 */
export function isRunNotificationSupported(): boolean {
	return 'Notification' in globalThis;
}

/**
 * The current notification permission.
 *
 * @returns The permission, or undefined where notifications are unsupported.
 */
export function getRunNotificationPermission(): NotificationPermission | undefined {
	return isRunNotificationSupported() ? Notification.permission : undefined;
}

/**
 * Prompts for notification permission, or resolves immediately with the
 * standing answer when the user has already decided.
 *
 * @returns The resulting permission, or undefined where notifications are unsupported.
 */
export async function requestRunNotificationPermission(): Promise<NotificationPermission | undefined> {
	if (!isRunNotificationSupported()) {
		return undefined;
	}
	if (Notification.permission !== 'default') {
		return Notification.permission;
	}
	return Notification.requestPermission();
}

/**
 * Raises a notification announcing that an agent run has finished, focusing the
 * originating tab when the user activates it. A no-op without permission.
 *
 * @param args.conversationLabel - Conversation the run belongs to, shown as the body.
 * @param args.conversationId - Distinguishes one conversation's notification from another's.
 */
export function showRunCompleteNotification(args: { conversationLabel: string; conversationId?: string }): void {
	if (getRunNotificationPermission() !== 'granted') {
		return;
	}
	const notification = new Notification('Agent finished', {
		body: args.conversationLabel,
		tag: `${NOTIFICATION_TAG_PREFIX}:${args.conversationId ?? 'unsaved'}`,
	});
	notification.onclick = () => {
		globalThis.focus();
		notification.close();
	};
}
